import {
  isValidPANCard,
  updateFunction,
  updateFunctionB2B,
  isValidPhone,
} from "@/lib/helper";
import { BriefcaseBusinessIcon, GraduationCapIcon } from "lucide-react";

export const partners = [
  "housr",
  "hw",
  "xu",
  "meridianstays",
  "nestaway",
  "stanza",
  "fabhostels",
  "kots",
  "oxotel",
  "circlepe",
  "truliv",
  "friendzo",
  "capacity_capital",
  "youthville",
  "urbanroomz",
  "flent",
  "blossomstayz",
  "oho",
];
export const withoutLeadPartners = [
  "housr",
  "hw",
  "xu",
  "meridianstays",
  "nestaway",
  "stanza",
  "fabhostels",
  "kots",
  "oxotel",
  "tribe",
  "circlepe",
  "truliv",
  "friendzo",
  "capacity_capital",
  "youthville",
  "urbanroomz",
  "flent",
  "blossomstayz",
  "oho",
];
// Partners that should NOT show the "Zero security deposit" welcome messaging.
// They fall back to a neutral subheading instead.
export const PARTNERS_WITHOUT_ZERO_DEPOSIT_MESSAGING = [
  "kots",
  "oho",
  "popcornstay",
];

// Welcome-screen subheading shown on the partner sign-up / login screens.
// Partners in PARTNERS_WITHOUT_ZERO_DEPOSIT_MESSAGING get a neutral line;
// everyone else keeps the default "Zero security deposit" messaging.
export const getWelcomeSubheading = (userIdentifier) =>
  PARTNERS_WITHOUT_ZERO_DEPOSIT_MESSAGING.includes(userIdentifier)
    ? "Start your journey with CirclePe"
    : "Journey to ZERO security deposit begins here";

export const ADD_MAINTENANCE_AMOUNT_SOURCES = [
  "housr",
  "xu",
  "meridianstays",
  "nestaway",
  // "truliv",
  "youthville",
  "urbanroomz",
  "flent",
];

export const PARTNER_LEAD_TYPES = {
  youthville: [
    {
      label: "student",
      value: "student",
      type: "card",
      icon: <GraduationCapIcon />,
      className: "",
    },
    {
      label: "salaried",
      value: "default",
      default: true,
      type: "card",
      icon: <BriefcaseBusinessIcon />,
      className: "",
    },
  ],
  fabhostels: [
    {
      label: "student",
      value: "student",
      type: "card",
      icon: <GraduationCapIcon />,
      className: "",
    },
    {
      label: "salaried",
      value: "salaried",
      default: true,
      type: "card",
      icon: <BriefcaseBusinessIcon />,
      className: "",
    },
  ],
  stanza: [
    {
      label: "student",
      value: "student",
      type: "card",
      icon: <GraduationCapIcon />,
      className: "",
    },
    {
      label: "salaried",
      value: "salaried",
      default: true,
      type: "card",
      icon: <BriefcaseBusinessIcon />,
      className: "",
    },
  ],
};

export const PACKAGE_PARTNERS = {
  youthville: {
    packages: [350000, 550000, 750000],
    defaultTenure: 9,
  },
  fabhostels: {
    packages: [100000, 105000, 111000, 120000],
    defaultTenure: 9,
  },
};

export const getMaintenanceInfo = (partner, city, monthlyRent) => {
  if (
    !partner ||
    !ADD_MAINTENANCE_AMOUNT_SOURCES.includes(partner.toLowerCase())
  ) {
    return { amount: null, tooltip: null };
  }

  const source = partner.toLowerCase();

  if (source === "housr") {
    return {
      amount: city?.toLowerCase() === "bangalore" ? 10000 : 5000,
      tooltip:
        "The Maintenance Cover/ Security Deposit is an amount CirclePe pays to Housr for potential accommodation damages. You'll only be charged this amount at the time of your move-out or opting out of CirclePe's services.",
    };
  } else if (source === "meridianstays") {
    return {
      amount: 5000,
      tooltip:
        "The Maintenance Cover/ Security Deposit is an amount CirclePe provides to Meridian Stays for potential accommodation damages. You'll only be charged if the Meridian Stays team confirms any damage during your move-out.",
    };
  } else if (source === "xu" && monthlyRent) {
    return {
      amount: 2 * monthlyRent,
      tooltip:
        "The Maintenance Cover is 2 times your monthly rent, which will be used for any potential accommodation damages.",
    };
  } else if (source === "nestaway" && monthlyRent) {
    return {
      amount: monthlyRent,
      tooltip:
        "The Maintenance Cover is your monthly rent, which will be used for any potential accommodation damages.",
    };
  } else if (source === "truliv") {
    return {
      amount: city?.toLowerCase() === "bangalore" ? 10000 : 5000,
      tooltip:
        "The Maintenance Cover is an amount CirclePe provides to Truliv for potential accommodation damages. You'll only be charged if the Truliv team confirms any damage during your move-out.",
    };
  } else if (source === "youthville") {
    return {
      amount: 12000,
      tooltip:
        "The Maintenance Cover is an amount CirclePe provides to Youthville Hostel for potential accommodation damages. You'll only be charged if the Youthville team confirms any damage during your move-out.",
    };
  } else if (source === "urbanroomz") {
    return {
      amount: 5000,
      tooltip:
        "The Maintenance Cover/ Security Deposit is an amount CirclePe pays to Urban Roomz for potential accommodation damages. You'll only be charged this amount at the time of your move-out or opting out of CirclePe's services.",
    };
  } else if (source === "flent") {
    return {
      amount: 10000,
      tooltip:
        "The Maintenance Cover/ Security Deposit is an amount CirclePe pays to Flent for potential accommodation damages. You'll only be charged this amount at the time of your move-out or opting out of CirclePe's services.",
    };
  }

  return { amount: null, tooltip: null };
};

export const partnerJourneys = {
  housr: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
      // nextPath: "rent-summary",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  nestaway: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
        // {
        //   type: "text",
        //   label: "First Name",
        //   name: "first_name",
        //   required: true,
        // },
        // {
        //   type: "text",
        //   label: "Last Name",
        //   name: "last_name",
        //   required: true,
        // },
        // {
        //   type: "number",
        //   label: "Approximate Rent",
        //   name: "rent",
        //   required: true,
        // },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
      // nextPath: "rent-summary",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  hw: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
      // nextPath: "rent-summary",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  meridianstays: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
      // nextPath: "rent-summary",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  xu: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  stanza: [
    {
      step: "guarantor-details",
      subHeading: "",
      heading: "Whats your employment status?",
      fields: [
        {
          type: "select",
          label: "Employment Status",
          name: "employment_status",
          options: [
            { value: "employed", label: "Salaried" },
            { value: "self-employed", label: "Self Employed" },
            { value: "student", label: "Student" },
          ],
          required: true,
        },
      ],
    },

    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
      // nextPath: "rent-summary",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  youthville: [
    {
      step: "guarantor-details",
      subHeading: "",
      heading: "Whats your employment status?",
      fields: [
        {
          type: "select",
          label: "Employment Status",
          name: "employment_status",
          options: [
            { value: "employed", label: "Salaried" },
            { value: "self-employed", label: "Self Employed" },
            { value: "student", label: "Student" },
          ],
          required: true,
        },
      ],
    },

    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
      // nextPath: "rent-summary",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  kots: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
      // nextPath: "rent-summary",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  oho: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
      // nextPath: "rent-summary",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  oxotel: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
      // nextPath: "rent-summary",
    },
  ],
  tribe: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
      // nextPath: "rent-summary",
    },
  ],
  circlepe: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
      // nextPath: "rent-summary",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  truliv: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
      // nextPath: "rent-summary",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  friendzo: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
      // nextPath: "rent-summary",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  capacity_capital: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
      // nextPath: "rent-summary",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  fabhostels: [
    {
      step: "guarantor-details",
      subHeading: "",
      heading: "Whats your employment status?",
      fields: [
        {
          type: "select",
          label: "Employment Status",
          name: "employment_status",
          options: [
            { value: "employed", label: "Salaried" },
            { value: "self-employed", label: "Self Employed" },
            { value: "student", label: "Student" },
          ],
          required: true,
        },
      ],
    },
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
      // nextPath: "rent-summary",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  urbanroomz: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  blossomstayz: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  flent: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
};

export const partnerNavigationRules = {
  housr: {
    rentSummary: "onboarding",
    congrats: "rent-summary",
    workDetails: "banking",
    esign: "mandate",
  },
  stanza: {
    rentSummary: "onboarding",
    congrats: "rent-summary",
    workDetails: "banking",
    esign: "mandate",
  },
  fabhostels: {
    rentSummary: "onboarding",
    congrats: "rent-summary",
    workDetails: "banking",
    esign: "mandate",
  },
  xu: {
    rentSummary: "onboarding",
    congrats: "rent-summary",
    workDetails: "banking",
    esign: "mandate",
  },
  hw: {
    rentSummary: "onboarding",
    congrats: "rent-summary",
    workDetails: "banking",
    esign: "mandate",
  },
  kots: {
    rentSummary: "onboarding",
    congrats: "rent-summary",
    workDetails: "banking",
    esign: "mandate",
  },
  oho: {
    rentSummary: "onboarding",
    congrats: "rent-summary",
    workDetails: "banking",
    esign: "mandate",
  },
  oxotel: {
    rentSummary: "onboarding",
    congrats: "rent-summary",
    workDetails: "banking",
    esign: "mandate",
  },
  nestaway: {
    rentSummary: "onboarding",
    congrats: "rent-summary",
    workDetails: "banking",
    esign: "mandate",
  },
  meridianstays: {
    rentSummary: "onboarding",
    congrats: "rent-summary",
    workDetails: "banking",
    esign: "mandate",
  },
  tribe: {
    rentSummary: "onboarding",
    congrats: "rent-summary",
    workDetails: "banking",
    esign: "mandate",
  },
  circlepe: {
    rentSummary: "onboarding",
    congrats: "rent-summary",
    workDetails: "banking",
    esign: "mandate",
  },
  truliv: {
    rentSummary: "onboarding",
    congrats: "rent-summary",
    workDetails: "banking",
    esign: "mandate",
  },
  friendzo: {
    rentSummary: "onboarding",
    congrats: "rent-summary",
    workDetails: "banking",
    esign: "mandate",
  },
  capacity_capital: {
    rentSummary: "onboarding",
    congrats: "rent-summary",
    workDetails: "banking",
    esign: "mandate",
  },
  urbanroomz: {
    rentSummary: "onboarding",
    congrats: "rent-summary",
    workDetails: "banking",
    esign: "mandate",
  },
  flent: {
    rentSummary: "onboarding",
    congrats: "rent-summary",
    workDetails: "banking",
    esign: "mandate",
  },
  blossomstayz: {
    rentSummary: "onboarding",
    congrats: "rent-summary",
    workDetails: "banking",
    esign: "mandate",
  },
};

export const partnerData = {
  housr: {
    noticePeriod: 30,
  },
  xu: {
    noticePeriod: 45,
  },
  hw: {
    noticePeriod: 30,
  },
  meridianstays: {
    noticePeriod: 30,
  },
  nestaway: {
    noticePeriod: 30,
  },
  stanza: {
    noticePeriod: 30,
  },
  fabhostels: {
    noticePeriod: 30,
  },
  kots: {
    noticePeriod: 45,
  },
  oho: {
    noticePeriod: 45,
  },
  oxotel: {
    noticePeriod: 30,
  },
  tribe: {
    noticePeriod: 30,
  },
  // truliv: {
  //   noticePeriod: 30,
  // },
  circlepe: {
    noticePeriod: 30,
  },
  friendzo: {
    noticePeriod: 30,
  },
  capacity_capital: {
    noticePeriod: 30,
  },
  youthville: {
    noticePeriod: 30,
  },
  urbanroomz: {
    noticePeriod: 30,
  },
  flent: {
    noticePeriod: 30,
  },
  blossomstayz: {
    noticePeriod: 30,
  },
};

export const navigationRules = {
  default: {
    phoneMismatch: "alt-phone",
    notHardEligible: "sorry",
    isHardEligible: "onboarding",
    isEligibleCongrats: "congrats",
    panNotChecked: "eligibility-check",
    workDetails: "work-details",
    bankingNotChecked: "banking",
    fromWorkDetailsTo: "banking",
    rentSummary: "rent-summary",
    propertyDetails: "property-details",
    guarantorDetails: "guarantor-details",
  },
  housr: {
    phoneMismatch: "alt-phone",
    notHardEligible: "sorry",
    isHardEligible: "onboarding",
    isEligibleCongrats: "congrats",
    panNotChecked: "eligibility-check",
    bankingNotChecked: "banking",
    fromWorkDetailsTo: "banking",
    rentSummary: "rent-summary",
    propertyDetails: "property-details",
    workDetails: "work-details",
  },
  stanza: {
    phoneMismatch: "alt-phone",
    notHardEligible: "sorry",
    isHardEligible: "onboarding",
    isEligibleCongrats: "congrats",
    panNotChecked: "eligibility-check",
    guarantorDetails: "guarantor-details",
    bankingNotChecked: "banking",
    fromWorkDetailsTo: "banking",
    rentSummary: "rent-summary",
    propertyDetails: "property-details",
    workDetails: "work-details",
  },
  fabhostels: {
    phoneMismatch: "alt-phone",
    notHardEligible: "sorry",
    isHardEligible: "onboarding",
    isEligibleCongrats: "congrats",
    panNotChecked: "eligibility-check",
    guarantorDetails: "guarantor-details",
    bankingNotChecked: "banking",
    fromWorkDetailsTo: "banking",
    rentSummary: "rent-summary",
    propertyDetails: "property-details",
    workDetails: "work-details",
  },
  hw: {
    phoneMismatch: "alt-phone",
    notHardEligible: "sorry",
    isHardEligible: "onboarding",
    isEligibleCongrats: "congrats",
    panNotChecked: "eligibility-check",
    bankingNotChecked: "banking",
    fromWorkDetailsTo: "banking",
    rentSummary: "congrats",
    propertyDetails: "property-details",
    workDetails: "work-details",
  },
  nestaway: {
    phoneMismatch: "alt-phone",
    notHardEligible: "sorry",
    isHardEligible: "onboarding",
    isEligibleCongrats: "congrats",
    panNotChecked: "eligibility-check",
    bankingNotChecked: "banking",
    fromWorkDetailsTo: "banking",
    rentSummary: "congrats",
    propertyDetails: "property-details",
    workDetails: "work-details",
  },
  meridianstays: {
    phoneMismatch: "alt-phone",
    notHardEligible: "sorry",
    isHardEligible: "onboarding",
    isEligibleCongrats: "congrats",
    panNotChecked: "eligibility-check",
    bankingNotChecked: "banking",
    fromWorkDetailsTo: "banking",
    rentSummary: "congrats",
    propertyDetails: "property-details",
    workDetails: "work-details",
  },
  xu: {
    phoneMismatch: "alt-phone",
    panChecked: "congrats",
    notHardEligible: "sorry",
    fromWorkDetailsTo: "banking",
    propertyDetails: "property-details",
    workDetails: "work-details",
  },
  kots: {
    phoneMismatch: "alt-phone",
    notHardEligible: "sorry",
    isHardEligible: "onboarding",
    isEligibleCongrats: "congrats",
    panNotChecked: "eligibility-check",
    bankingNotChecked: "banking",
    fromWorkDetailsTo: "banking",
    rentSummary: "rent-summary",
    propertyDetails: "property-details",
    workDetails: "work-details",
  },
  oho: {
    phoneMismatch: "alt-phone",
    notHardEligible: "sorry",
    isHardEligible: "onboarding",
    isEligibleCongrats: "congrats",
    panNotChecked: "eligibility-check",
    bankingNotChecked: "banking",
    fromWorkDetailsTo: "banking",
    rentSummary: "rent-summary",
    propertyDetails: "property-details",
    workDetails: "work-details",
  },
  oxotel: {
    phoneMismatch: "alt-phone",
    notHardEligible: "sorry",
    isHardEligible: "onboarding",
    isEligibleCongrats: "congrats",
    panNotChecked: "eligibility-check",
    bankingNotChecked: "banking",
    fromWorkDetailsTo: "banking",
    rentSummary: "rent-summary",
    propertyDetails: "property-details",
    workDetails: "work-details",
  },
  tribe: {
    phoneMismatch: "alt-phone",
    notHardEligible: "sorry",
    isHardEligible: "onboarding",
    isEligibleCongrats: "congrats",
    panNotChecked: "eligibility-check",
    bankingNotChecked: "banking",
    fromWorkDetailsTo: "banking",
    rentSummary: "rent-summary",
    propertyDetails: "property-details",
    workDetails: "work-details",
  },
  circlepe: {
    phoneMismatch: "alt-phone",
    notHardEligible: "sorry",
    isHardEligible: "onboarding",
    isEligibleCongrats: "congrats",
    panNotChecked: "eligibility-check",
    bankingNotChecked: "banking",
    fromWorkDetailsTo: "banking",
    rentSummary: "rent-summary",
    propertyDetails: "property-details",
    workDetails: "work-details",
  },
  truliv: {
    phoneMismatch: "alt-phone",
    notHardEligible: "sorry",
    isHardEligible: "onboarding",
    isEligibleCongrats: "congrats",
    panNotChecked: "eligibility-check",
    bankingNotChecked: "banking",
    fromWorkDetailsTo: "banking",
    rentSummary: "rent-summary",
    propertyDetails: "property-details",
    workDetails: "work-details",
  },
  friendzo: {
    phoneMismatch: "alt-phone",
    notHardEligible: "sorry",
    isHardEligible: "onboarding",
    isEligibleCongrats: "congrats",
    panNotChecked: "eligibility-check",
    bankingNotChecked: "banking",
    fromWorkDetailsTo: "banking",
    rentSummary: "rent-summary",
    propertyDetails: "property-details",
    workDetails: "work-details",
  },
  capacity_capital: {
    phoneMismatch: "alt-phone",
    notHardEligible: "sorry",
    isHardEligible: "onboarding",
    isEligibleCongrats: "congrats",
    panNotChecked: "eligibility-check",
    bankingNotChecked: "banking",
    fromWorkDetailsTo: "banking",
    rentSummary: "rent-summary",
    propertyDetails: "property-details",
    workDetails: "work-details",
  },
  youthville: {
    phoneMismatch: "alt-phone",
    notHardEligible: "sorry",
    isHardEligible: "onboarding",
    isEligibleCongrats: "congrats",
    panNotChecked: "eligibility-check",
    bankingNotChecked: "banking",
    fromWorkDetailsTo: "banking",
    guarantorDetails: "guarantor-details",
    rentSummary: "rent-summary",
    propertyDetails: "property-details",
    workDetails: "work-details",
  },
  urbanroomz: {
    phoneMismatch: "alt-phone",
    notHardEligible: "sorry",
    isHardEligible: "onboarding",
    isEligibleCongrats: "congrats",
    panNotChecked: "eligibility-check",
    bankingNotChecked: "banking",
    fromWorkDetailsTo: "banking",
    rentSummary: "rent-summary",
    propertyDetails: "property-details",
    workDetails: "work-details",
  },
  flent: {
    phoneMismatch: "alt-phone",
    notHardEligible: "sorry",
    isHardEligible: "onboarding",
    isEligibleCongrats: "congrats",
    panNotChecked: "eligibility-check",
    bankingNotChecked: "banking",
    fromWorkDetailsTo: "banking",
    rentSummary: "rent-summary",
    propertyDetails: "property-details",
    workDetails: "work-details",
  },
  blossomstayz: {
    phoneMismatch: "alt-phone",
    notHardEligible: "sorry",
    isHardEligible: "onboarding",
    isEligibleCongrats: "congrats",
    panNotChecked: "eligibility-check",
    bankingNotChecked: "banking",
    fromWorkDetailsTo: "banking",
    rentSummary: "rent-summary",
    propertyDetails: "property-details",
    workDetails: "work-details",
  },
};

export const withoutLeadPartnerJourney = {
  xu: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunction,
      nextPath: "congrats",
      apiPath: "approval/check",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  hw: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunction,
      nextPath: "congrats",
      apiPath: "approval/check",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  nestaway: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunction,
      nextPath: "congrats",
      apiPath: "approval/check",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  meridianstays: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunction,
      nextPath: "congrats",
      apiPath: "approval/check",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  oxotel: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunction,
      nextPath: "congrats",
      apiPath: "approval/check",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  tribe: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunction,
      nextPath: "congrats",
      apiPath: "approval/check",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  circlepe: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunction,
      nextPath: "congrats",
      apiPath: "approval/check",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  friendzo: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "approval/check",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  capacity_capital: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "approval/check",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
  youthville: [
    {
      step: "eligibility-check",
      heading: "Quick Eligibility Check",
      subHeading: "Does not affect your credit score",
      fields: [
        {
          type: "pan",
          label: "PAN Number",
          name: "pan_number",
          required: true,
        },
      ],
      validate: (formData) => isValidPANCard(formData.pan_number),
      onSubmit: updateFunctionB2B,
      nextPath: "congrats",
      apiPath: "approval/check",
      click_name: "Confirm",
      click_source: "Eligibility Check Screen",
    },
    {
      step: "alt-phone",
      heading: "Phone Number Mismatch",
      subHeading: "Don't worry, please provide the phone number ending with ",
      fields: [
        {
          type: "tel",
          label: "Alt Phone",
          name: "alt_phone",
          required: true,
          consent: true,
          maxLen: 10,
        },
      ],
      validate: (formData) => isValidPhone(formData.alt_phone),
      onSubmit: updateFunctionB2B,
      nextPath: "redirect",
      apiPath: "b2b/onboard/check-eligibility",
      click_name: "Confirm",
      click_source: "Alt Phone Screen",
    },
  ],
};
